import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsersProvider } from '../../../users/providers/users-provider';
import { Title } from '@angular/platform-browser';
import { FilesProvider } from '../../../files/providers/files-provider';
import { GuestsProvider, GuestRow } from '../../providers/guests-provider';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose, MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfigProvider } from "../../../config-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { AthletesProvider } from "../../../athletes/providers/athletes-provider";
import { LangsProvider } from "../../../common/providers/langs-provider";
import { Lang } from "../../../common/models/lang";
import { User } from "../../../common/models/user";
import { Organization } from "../../../common/models/organization";
import { Observable, ReplaySubject, Subject, takeUntil } from "rxjs";
import { AsyncPipe, NgForOf, NgIf } from "@angular/common";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatOption } from "@angular/material/autocomplete";
import { MatFormField, MatSelect } from "@angular/material/select";
import { MatProgressBar } from "@angular/material/progress-bar";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIcon } from "@angular/material/icon";
import { MatError } from "@angular/material/form-field";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { take } from "rxjs/operators";

@Component({
  selector: 'guests-athletes-form',
  providers: [],
  styleUrls: ['./athletes.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogClose,
    MatDialogTitle,
    NgIf,
    NgForOf,
    MatButton,
    MatIconButton,
    MatOption,
    MatSelect,
    MatFormField,
    MatProgressBar,
    MatTooltip,
    TranslateModule,
    MatDialogActions,
    MatDialogContent,
    MatIcon,
    AsyncPipe,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './athletes.html'
})
export class GuestsAthletesFormComponent implements OnInit, AfterViewInit, OnDestroy {
  public isLoading = false;
  public formTitle = '';

  public organisation: Organization | null = null;
  public guestRow: GuestRow | null = null;
  public guestId = 0;
  public user: User = new User({});

  public langId: number = Lang.ID_DEFAULT;

  @ViewChild('multiSelect', { static: true }) multiSelect!: MatSelect;
  public availableAthletes: User[] = [];
  public athletesSelected: User[] = [];
  public athletesSelectFormControl: FormControl<User[]> = new FormControl<User[]>([]) as FormControl<User[]>;
  public athleteFilterFormControl: FormControl<string> = new FormControl<string>('') as FormControl<string>;
  public filteredAthletes: ReplaySubject<User[]> = new ReplaySubject<User[]>(1);
  protected _onDestroy = new Subject<void>();

  constructor(
    public conf: ConfigProvider,
    public currentUser: CurrentUser,
    public utils: UtilsProvider,
    public dialog: MatDialogRef<GuestsAthletesFormComponent>,
    public usersProvider: UsersProvider,
    private _toaster: MatSnackBar,
    private _title: Title,
    private _athletesProvider: AthletesProvider,
    private _router: Router,
    private _route: ActivatedRoute,
    private _langsProvider: LangsProvider,
    private _translater: TranslateService,
    private _files: FilesProvider,
    private _guestsProvider: GuestsProvider,
    @Inject(MAT_DIALOG_DATA) private _data: any
  ) {
    this.availableAthletes = _data.availableAthletes;
    this.organisation = _data.organization;
    this.formTitle = this._data.formTitle;

    if (this._data.guestRow) {

      this.guestRow = this._data.guestRow;
      this.guestId = this._data.guestRow.guest.id;
      this.user = this._data.guestRow.user;
      this.langId = this._data.guestRow.user.langId;
      // get all user from guestRow.athletes.user and put them in athletesSelected
      this.athletesSelected = this._data.guestRow.athletes;
    }
  }

  public ngOnInit() {
    // Charger les coachs filtrés initialement
    this.filteredAthletes.next(this.availableAthletes.slice());

    // Écouter les changements dans le champ de recherche
    this.athleteFilterFormControl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterAthletes();
      });

    // Initialiser les coachs sélectionnés si nécessaire
    if (this.athletesSelected.length > 0) {
      this.athletesSelectFormControl.setValue(this.athletesSelected);
    }
  }

  public ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  public ngAfterViewInit() {
    this.setInitialValue();
  }

  public onSaveClicked(): void {
    if (this.canSave()) {
      this.isLoading = true;
      this.save().subscribe({
        next: (result: GuestRow) => {
          this._toaster.open(this._translater.instant('Guest_saved'), '', {duration: 5000});
          this.isLoading = false;
          this.dialog.close(result);
        },
        error: (error: any) => this.onError(error)
      });
    } else {
      this.touchFields();
      this._toaster.open(this._translater.instant('Correct_form_errors'), '', {duration: 5000});
    }
  }

  public canSave(): boolean {
    return !this.athletesSelectFormControl.invalid;
  }

  private touchFields(): void {
    this.athletesSelectFormControl.markAsTouched();
  }

  private save(): Observable<GuestRow> {
    const guestRow: GuestRow = {
      user: this.guestRow!.user,
      guest: this.guestRow!.guest,
      athletes: [],
      athleteNames: [],
      teams: [],
      teamNames: []
    };

    if (this.athletesSelectFormControl.value.length) {
      for (let i = 0; i < this.athletesSelectFormControl.value.length; i++) {

        const athleteUser = this.utils.findIn(
          this.athletesSelectFormControl.value[i].id,
          this.availableAthletes,
          'id');
        if (athleteUser) {
          guestRow.athletes.push(athleteUser);
        }
      }
    }
    if (!this.organisation?.id) throw new Error('No organisation id');
    return this._guestsProvider.saveGuestAthletes(this.organisation.id, guestRow)
  }

  protected setInitialValue() {
    this.filteredAthletes
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.multiSelect.compareWith = (a: User, b: User) => a && b && a.id === b.id;
      });
  }

  protected filterAthletes() {
    if (!this.availableAthletes) {
      return;
    }

    const search = this.athleteFilterFormControl.value?.toLowerCase() || '';
    if (!search) {
      this.filteredAthletes.next(this.availableAthletes.slice());
      return;
    }

    this.filteredAthletes.next(
      this.availableAthletes.filter((athlete) =>
        `${athlete.firstName} ${athlete.lastName}`.toLowerCase().includes(search)
      )
    );
  }
  private onError(error: any): void {
    this.isLoading = false;

    if (error.status === 422) {
      if (error.error.errors && error.error.errors.email) {
        // c'est qu'on a essayé de faire valider un email test@test par exemple (c'est validé par le validator angular mais pas laravel...)
        return;
      }
    }

    this._toaster.open(this._translater.instant('Error_unknown'), '', {duration: 5000});
  }

}
